import { makeStyles } from "@material-ui/core";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Select } from "antd";
import { REPORT_FONT, useReportFonts } from "../reportFonts";
import BackButton from "../BackButton";
import { goToReportsCatalog } from "../reportNav";
import { useHistory, useParams } from "react-router-dom";
import {
  FILTERS_CONFIG,
  initFilterValues,
  isFilterActive,
  getDisplayValue,
  getDefaultValue,
  getActiveFilterCount,
  areVisibleFilterValuesEqual,
  HorizontalDateRangeFilter,
  FilterApplyButton,
} from "./GlobalFilterPanel";
import { useExpandedAppliedFilters } from "./useExpandedAppliedFilters";
import {
  applyCascadeDraftChange,
  pruneInvalidCascadeSelections,
  getFilterFieldOptions,
  toFilterSelectOptions,
  FILTER_SELECT_SEARCH_PROPS,
  GlobalMultiSelectFilter,
} from "./filterRelationshipUtils";
import {
  buildWaterfallEacApiFilters,
  getApiFilterKey,
} from "./waterfall-eac/waterfallEacFilterUtils";
import {
  getScenarioYoyTwoYearComparisonAPI,
  getScenarioYoyFullPlanOverYearAPI,
  downloadScenarioYoyTwoYearComparisonAPI,
  downloadScenarioYoyFullPlanOverYearAPI,
} from "../../../api/reports-api";
import Loading from "../../../common-components/Loading";
import {
  usePlByProgramStyles,
  PlPageHeader,
  PL_PAGE_BG,
} from "./pl-by-program/plByProgramTheme";
import CurrentYearToNextTabComponent from "./scenario-yoy/CurrentYearToNext";
import FullPlanTabComponent from "./scenario-yoy/FullPlanOverYear";

import {
  TH_BG,
  TH_BG_BAND,
} from "../reportTableConfig";

const MONTH_ABBR = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/* ── API response → component prop transforms ── */
function mapYearBlock(prefix, block) {
  const b = block || {};
  return {
    [`r${prefix}`]: b.revenue ?? null,
    [`dl${prefix}`]: b.directLabor ?? null,
    [`sub${prefix}`]: b.subcontractor ?? null,
    [`odc${prefix}`]: b.odcs ?? null,
    [`tdc${prefix}`]: b.totalDirectCost ?? null,
    [`gp${prefix}`]: b.grossProfit ?? null,
    [`gm${prefix}`]: b.grossMarginPct != null ? b.grossMarginPct * 100 : null,
  };
}

function mapTwoYearContract(c) {
  return {
    id: c.contractId,
    contractName: c.contractName,
    bu: c.businessUnit,
    ...mapYearBlock("26", c.year1),
    ...mapYearBlock("27", c.year2),
    ...mapYearBlock("var", c.variance),
  };
}

function transformTwoYear(data) {
  if (!data || !Array.isArray(data.contracts)) return null;
  return {
    fiscalYear1: data.fiscalYear1,
    fiscalYear2: data.fiscalYear2,
    actualThroughMonth: data.actualThroughMonth,
    rows: data.contracts.map(mapTwoYearContract),
    totalRow: {
      ...mapYearBlock("26", data.grandTotals?.year1),
      ...mapYearBlock("27", data.grandTotals?.year2),
      ...mapYearBlock("var", data.grandTotals?.variance),
    },
  };
}

const FP_HOURS_KEYS = new Set([
  "totalProjectHours",
  "ibEmployeeHours",
  "subkHours",
]);

function transformFullPlan(data) {
  if (!data || !Array.isArray(data.rows)) return null;
  const years = Array.isArray(data.years) ? data.years : [];
  const rows = data.rows.map((r) => {
    const isPct = !!r.isPct || r.lineItemKey === "grossMarginPct";
    const isFte = r.lineItemKey === "fte";
    const isHours = FP_HOURS_KEYS.has(r.lineItemKey);
    const isCurrency = !isPct && !isHours && !isFte;
    const scale = isPct ? 100 : 1;
    const scaleVal = (v) => (v == null ? null : v * scale);
    return {
      label: r.element,
      lineItemKey: r.lineItemKey,
      isPct,
      isCurrency,
      dp: isFte ? 2 : isHours ? 1 : undefined,
      bold: r.lineItemKey === "totalDirectCost",
      single: true,
      ys: years.map((y) => scaleVal(r.byYear?.[String(y)])),
      total: scaleVal(r.total),
      itd: scaleVal(r.itd),
    };
  });
  return { years, rows, actualThroughMonth: data.actualThroughMonth };
}

/* Global filters exposed for Scenario Comparison YoY. Full Plan Over Year
   additionally drops Year since it already shows every year as its own
   column. */
const YOY_FILTERS = [
  "contractId",
  "projectId",
  "year",
  "bu",
  "popEndDate",
  "businessType",
];

const NAV = "#0D2137";
const HEADER_BG = TH_BG_BAND;
const BAND_26 = TH_BG_BAND;
const BAND_27 = "#162032";
const BAND_VAR = TH_BG;
const TAB_ACTIVE = "#4F46E5";

const TABS = [
  { key: "current_to_next", label: "Current Year to Next Year" },
  { key: "full_plan", label: "Full Plan over Year" },
];

/* Per-tab download endpoint + output filename */
const DOWNLOAD_BY_TAB = {
  current_to_next: { api: downloadScenarioYoyTwoYearComparisonAPI, filename: "two-year-comparison.xlsx" },
  full_plan: { api: downloadScenarioYoyFullPlanOverYearAPI, filename: "full-plan-over-year.xlsx" },
};

/* ══════════════════════════════════════════════════════════════ */
/*  Data                                                          */
/* ══════════════════════════════════════════════════════════════ */
// N = null (blank cell)
// NOTE: CYTN_ROWS / CYTN_TOTAL / FP_YEARS / FP_ROWS below are legacy sample
// data retained for reference only. Live data now comes from the API.
// eslint-disable-next-line no-unused-vars
const N = null;

// eslint-disable-next-line no-unused-vars
const CYTN_ROWS = [
  {
    id: "01112",
    contractName: "EFIT",
    projId: "00001.02.04.01.02.03",
    bu: "Navy",
    r26: 26678,
    dl26: N,
    sub26: 34900,
    odc26: N,
    tdc26: 34900,
    gp26: -8222,
    gm26: -30.8,
    r27: N,
    dl27: N,
    sub27: N,
    odc27: N,
    tdc27: N,
    gp27: N,
    gm27: N,
  },
  {
    id: "01115",
    contractName: "GSA DIGIT (SUB TO NCI)",
    projId: "00001.01.03.01.04.06",
    bu: "Citizen Services",
    r26: 2057769,
    dl26: 1080639,
    sub26: N,
    odc26: N,
    tdc26: 1080639,
    gp26: 977130,
    gm26: 47.5,
    r27: N,
    dl27: N,
    sub27: N,
    odc27: N,
    tdc27: 0,
    gp27: 0,
    gm27: 0.0,
  },
  {
    id: "01117",
    contractName: "NAV E&I (SUB TO NCI)",
    projId: "00001.02.04.01.02.03",
    bu: "Navy",
    r26: 141686,
    dl26: 90576,
    sub26: N,
    odc26: 5066,
    tdc26: 95642,
    gp26: 46044,
    gm26: 32.5,
    r27: N,
    dl27: N,
    sub27: N,
    odc27: N,
    tdc27: N,
    gp27: N,
    gm27: N,
  },
  {
    id: "01118",
    contractName: "DHS OIG ITSS (STEAMPUNK)",
    projId: "00001.01.02.03.03.01",
    bu: "Dept of Homeland Security",
    r26: N,
    dl26: N,
    sub26: N,
    odc26: N,
    tdc26: 0,
    gp26: 0,
    gm26: 0.0,
    r27: N,
    dl27: N,
    sub27: N,
    odc27: N,
    tdc27: N,
    gp27: N,
    gm27: N,
  },
  {
    id: "01122",
    contractName: "TSA ESVP POMS (FMT)",
    projId: "00001.01.02.03.03.01",
    bu: "Dept of Homeland Security",
    r26: 1331445,
    dl26: 769812,
    sub26: 47911,
    odc26: N,
    tdc26: 817722,
    gp26: 513723,
    gm26: 38.6,
    r27: 637819,
    dl27: 365150,
    sub27: N,
    odc27: N,
    tdc27: 365150,
    gp27: 272669,
    gm27: 42.8,
  },
  {
    id: "01123",
    contractName: "USDA ELM 4 OLYMPIA (EHS)",
    projId: "00001.01.03.01.04.03",
    bu: "Citizen Services",
    r26: 347219,
    dl26: 207288,
    sub26: N,
    odc26: N,
    tdc26: 207288,
    gp26: 139931,
    gm26: 40.3,
    r27: N,
    dl27: N,
    sub27: N,
    odc27: N,
    tdc27: N,
    gp27: N,
    gm27: N,
  },
  {
    id: "01124",
    contractName: "USDA ELM 4 TSPI",
    projId: "00001.01.03.01.04.03",
    bu: "Citizen Services",
    r26: 300952,
    dl26: 277006,
    sub26: N,
    odc26: N,
    tdc26: 277006,
    gp26: 23946,
    gm26: 8.0,
    r27: N,
    dl27: N,
    sub27: N,
    odc27: N,
    tdc27: N,
    gp27: N,
    gm27: N,
  },
  {
    id: "01125",
    contractName: "CBP HIEOps",
    projId: "00001.01.02.03.02.05",
    bu: "Dept of Homeland Security",
    r26: 3489700,
    dl26: 1915099,
    sub26: N,
    odc26: 261,
    tdc26: 1915360,
    gp26: 1574340,
    gm26: 45.1,
    r27: 3014782,
    dl27: 1582651,
    sub27: N,
    odc27: N,
    tdc27: 1582651,
    gp27: 1432131,
    gm27: 47.5,
  },
  {
    id: "01128",
    contractName: "DHS USCIS RMSS - CGI",
    projId: "00001.01.02.03.02.02",
    bu: "Dept of Homeland Security",
    r26: 8963010,
    dl26: 4491820,
    sub26: 581891,
    odc26: 13333,
    tdc26: 5087044,
    gp26: 3875966,
    gm26: 43.2,
    r27: 9977110,
    dl27: 5108419,
    sub27: 812329,
    odc27: N,
    tdc27: 5920748,
    gp27: 4056362,
    gm27: 40.7,
  },
  {
    id: "01129",
    contractName: "USARC CYBER SERV (INDIGO)",
    projId: "00001.02.03.01.04.01",
    bu: "Army",
    r26: 793599,
    dl26: 470049,
    sub26: N,
    odc26: 3140,
    tdc26: 473189,
    gp26: 320410,
    gm26: 40.4,
    r27: 790636,
    dl27: 481154,
    sub27: N,
    odc27: N,
    tdc27: 481154,
    gp27: 309483,
    gm27: 39.1,
  },
  {
    id: "01130",
    contractName: "USAF CAMPS SUST (NETRIST)",
    projId: "00001.02.02.01.03.01",
    bu: "Air Force",
    r26: 986697,
    dl26: 527595,
    sub26: N,
    odc26: N,
    tdc26: 527595,
    gp26: 459102,
    gm26: 46.5,
    r27: 956440,
    dl27: 534500,
    sub27: N,
    odc27: N,
    tdc27: 534500,
    gp27: 421939,
    gm27: 44.1,
  },
  {
    id: "01131",
    contractName: "FBI_RADIO_PRG_ENG TC SRV",
    projId: "00001.01.02.02.03.06",
    bu: "Federal Law Enforcement",
    r26: 602477,
    dl26: 352036,
    sub26: N,
    odc26: N,
    tdc26: 352036,
    gp26: 250441,
    gm26: 41.6,
    r27: 713124,
    dl27: 422870,
    sub27: N,
    odc27: N,
    tdc27: 422870,
    gp27: 290254,
    gm27: 40.7,
  },
  {
    id: "01134",
    contractName: "DOD NAWCWD CSSS (NDTI)",
    projId: "00001.02.04.01.02.03",
    bu: "Navy",
    r26: 360240,
    dl26: 196413,
    sub26: N,
    odc26: N,
    tdc26: 196413,
    gp26: 163827,
    gm26: 45.5,
    r27: 450973,
    dl27: 243303,
    sub27: N,
    odc27: N,
    tdc27: 243303,
    gp27: 207670,
    gm27: 46.0,
  },
  {
    id: "01135",
    contractName: "BIXAL SEC BPA",
    projId: "00001.01.03.01.05.04",
    bu: "Citizen Services",
    r26: 1135825,
    dl26: 472918,
    sub26: N,
    odc26: 3312,
    tdc26: 476230,
    gp26: 659595,
    gm26: 58.1,
    r27: 1185509,
    dl27: 548861,
    sub27: N,
    odc27: N,
    tdc27: 548861,
    gp27: 636648,
    gm27: 53.7,
  },
  {
    id: "01137",
    contractName: "Tomahawk Tapestry Solutio",
    projId: "00001.02.04.01.02.03",
    bu: "Navy",
    r26: 1532446,
    dl26: 737744,
    sub26: N,
    odc26: N,
    tdc26: 737744,
    gp26: 794702,
    gm26: 51.9,
    r27: N,
    dl27: N,
    sub27: N,
    odc27: N,
    tdc27: N,
    gp27: N,
    gm27: N,
  },
  {
    id: "01138",
    contractName: "MEIS SOM",
    projId: "00001.02.01.03.01",
    bu: "Air Force",
    r26: 2475454,
    dl26: 1259163,
    sub26: N,
    odc26: N,
    tdc26: 1259163,
    gp26: 1216291,
    gm26: 49.1,
    r27: 2375963,
    dl27: 1245808,
    sub27: N,
    odc27: N,
    tdc27: 1245808,
    gp27: 1130155,
    gm27: 47.6,
  },
  {
    id: "01140",
    contractName: "FBI_SOAPS_TO106BRDG",
    projId: "00001.01.02.02.03.02",
    bu: "Federal Law Enforcement",
    r26: 526319,
    dl26: 215825,
    sub26: 166768,
    odc26: 6043,
    tdc26: 388636,
    gp26: 137684,
    gm26: 26.2,
    r27: N,
    dl27: N,
    sub27: N,
    odc27: N,
    tdc27: N,
    gp27: N,
    gm27: N,
  },
  {
    id: "01141",
    contractName: "SN.AF.ESS",
    projId: "00001.02.02.01.03.01",
    bu: "Air Force",
    r26: 1684106,
    dl26: 879550,
    sub26: N,
    odc26: N,
    tdc26: 879550,
    gp26: 804556,
    gm26: 47.8,
    r27: 1657633,
    dl27: 896624,
    sub27: N,
    odc27: N,
    tdc27: 896624,
    gp27: 761009,
    gm27: 45.9,
  },
  {
    id: "01143",
    contractName: "DOS_OBO_CTO_FOLLOW ON",
    projId: "00001.01.02.02.05.01",
    bu: "Federal Law Enforcement",
    r26: 408840,
    dl26: 216748,
    sub26: N,
    odc26: N,
    tdc26: 216748,
    gp26: 192092,
    gm26: 47.0,
    r27: N,
    dl27: 9424,
    sub27: N,
    odc27: N,
    tdc27: 9424,
    gp27: -9424,
    gm27: 0.0,
  },
  {
    id: "01144",
    contractName: "VA CDS (COFORMA)",
    projId: "00001.02.04.01.03.01",
    bu: "CCMD/4th Estate",
    r26: 514552,
    dl26: 260933,
    sub26: N,
    odc26: N,
    tdc26: 260933,
    gp26: 253619,
    gm26: 49.3,
    r27: 356135,
    dl27: 184731,
    sub27: N,
    odc27: N,
    tdc27: 184731,
    gp27: 171404,
    gm27: 48.1,
  },
  {
    id: "01145",
    contractName: "FBI_ITSSS_STOR184 BRDG",
    projId: "00001.01.02.02.03.07",
    bu: "Federal Law Enforcement",
    r26: 52019,
    dl26: 37989,
    sub26: N,
    odc26: N,
    tdc26: 37989,
    gp26: 14030,
    gm26: 27.0,
    r27: N,
    dl27: N,
    sub27: N,
    odc27: N,
    tdc27: N,
    gp27: N,
    gm27: N,
  },
  {
    id: "01147",
    contractName: "P1 PB DSO SPT",
    projId: "00001.02.02.01.02.04",
    bu: "Air Force",
    r26: 715533,
    dl26: 339548,
    sub26: N,
    odc26: N,
    tdc26: 339548,
    gp26: 375985,
    gm26: 52.5,
    r27: N,
    dl27: N,
    sub27: N,
    odc27: N,
    tdc27: N,
    gp27: N,
    gm27: N,
  },
  {
    id: "01149",
    contractName: "SOCOM DTS (IEX)",
    projId: "00001.02.04.01.03.01",
    bu: "CCMD/4th Estate",
    r26: 72027,
    dl26: 41654,
    sub26: N,
    odc26: N,
    tdc26: 41654,
    gp26: 30373,
    gm26: 42.2,
    r27: N,
    dl27: N,
    sub27: N,
    odc27: N,
    tdc27: N,
    gp27: N,
    gm27: N,
  },
];

// eslint-disable-next-line no-unused-vars
const CYTN_TOTAL = {
  r26: 368620360,
  dl26: 148100285,
  sub26: 73915406,
  odc26: 5115532,
  tdc26: 227131223,
  gp26: 141489137,
  gm26: 38.4,
  r27: 410623430,
  dl27: 161539816,
  sub27: 86814243,
  odc27: 1562562,
  tdc27: 249916621,
  gp27: 160706809,
  gm27: 39.1,
};

/* ══════════════════════════════════════════════════════════════ */
/*  Styles                                                        */
/* ══════════════════════════════════════════════════════════════ */
const useStyles = makeStyles(() => ({
  page: {
    fontFamily: REPORT_FONT,
    backgroundColor: PL_PAGE_BG,
    display: "flex",
    flexDirection: "column",
    minHeight: "calc(100vh - 80px)",
  },
  topBar: {
    background: "#fff",
    padding: "12px 24px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexShrink: 0,
  },
  crumb: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    color: "#9CA3AF",
    minWidth: 0,
  },
  crumbLink: {
    cursor: "pointer",
    color: "#9CA3AF",
    "&:hover": { color: "#6B7280" },
  },
  crumbSep: { color: "#D1D5DB", userSelect: "none" },
  crumbCur: { color: "#111827", fontWeight: 500 },
  topActions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginLeft: "auto",
    flexShrink: 0,
  },
  actionBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 32,
    padding: "0 12px",
    borderRadius: 6,
    border: "1px solid #E5E7EB",
    background: "#fff",
    color: "#374151",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    transition: "background 0.15s, border-color 0.15s",
    "&:hover": { background: "#F9FAFB", borderColor: "#D1D5DB" },
  },
  actionBtnActive: {
    borderColor: "#4F46E5",
    background: "#4F46E5",
    color: "#fff",
    "&:hover": { background: "#4338CA", borderColor: "#4338CA" },
  },
  actionBtnIconOnly: {
    width: 32,
    minWidth: 32,
    padding: 0,
    justifyContent: "center",
  },
  filterBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 18,
    height: 18,
    padding: "0 5px",
    borderRadius: 9,
    background: "rgba(255,255,255,0.25)",
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1,
  },

  /* ── horizontal filter bar (matches Scenario MoM) ── */
  hFilterBar: {
    position: "relative",
    background: "#d6dbf8",
    borderTop: "2px solid #191b8c",
    borderBottom: "1px solid #E5E7EB",
    padding: "12px 52px 12px 24px",
    display: "flex",
    alignItems: "flex-end",
    flexWrap: "wrap",
    gap: 10,
    flexShrink: 0,
  },
  hFilterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    flexShrink: 0,
    background: "#fff",
    borderRadius: 8,
    padding: "6px 10px 8px",
    minWidth: 120,
  },
  hFilterLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#191b8c",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    whiteSpace: "nowrap",
  },
  hFilterInput: {
    height: 26,
    padding: "0 6px",
    border: "none",
    borderRadius: 4,
    fontSize: 12,
    fontFamily: REPORT_FONT,
    color: "#111827",
    outline: "none",
    minWidth: 110,
    background: "transparent",
    "&:focus": { outline: "1px solid #4F46E5", borderRadius: 4 },
  },
  hFilterActions: {
    display: "flex",
    alignItems: "flex-end",
    gap: 8,
    marginLeft: "auto",
    flexShrink: 0,
  },
  hFilterApply: {
    height: 34,
    padding: "0 16px",
    borderRadius: 6,
    border: "none",
    background: "#4F46E5",
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: REPORT_FONT,
    whiteSpace: "nowrap",
    "&:hover": { background: "#4338CA" },
  "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  "&:disabled:hover": { background: "inherit" },
  },
  hFilterClearAll: {
    alignSelf: "flex-end",
    height: 34,
    padding: "0 12px",
    borderRadius: 6,
    border: "1px solid #E5E7EB",
    background: "#fff",
    color: "#374151",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    flexShrink: 0,
    whiteSpace: "nowrap",
    fontFamily: REPORT_FONT,
    "&:hover": { color: "#4F46E5", borderColor: "#4F46E5" },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  },
  hFilterClose: {
    position: "absolute",
    top: 10,
    right: 10,
    width: 28,
    height: 28,
    borderRadius: "50%",
    border: "2px solid #191b8c",
    background: TH_BG_BAND,
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: REPORT_FONT,
    "&:hover": { background: "#111827" },
  },

  /* ── active filter chips ── */
  chipsBar: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 16px",
    background: "#F8F9FF",
    borderBottom: "1px solid #E5E7EB",
    flexWrap: "wrap",
    flexShrink: 0,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    height: 26,
    padding: "0 10px",
    borderRadius: 20,
    background: TH_BG_BAND,
    border: `1px solid ${TH_BG_BAND}`,
    fontSize: 11,
    fontWeight: 500,
    color: "#F1F5F9",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  chipLabel: { color: "#94A3B8", fontWeight: 400, marginRight: 2 },
  chipClear: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 14,
    height: 14,
    borderRadius: "50%",
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#94A3B8",
    fontSize: 10,
    padding: 0,
    marginLeft: 2,
    "&:hover": { background: "rgba(255,255,255,0.15)", color: "#fff" },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  },
  chipsClearAll: {
    fontSize: 11,
    fontWeight: 600,
    color: "#6B7280",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "0 6px",
    marginLeft: 4,
    whiteSpace: "nowrap",
    flexShrink: 0,
    "&:hover": { color: "#4F46E5" },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  },

  tabBar: {
    background: "#fff",
    borderBottom: "1px solid #E5E7EB",
    display: "flex",
    flexWrap: "wrap",
    flexShrink: 0,
    padding: "0 24px",
    gap: "4px 0",
  },
  tab: {
    padding: "12px 16px",
    fontSize: 14,
    fontWeight: 500,
    color: "#374151",
    cursor: "pointer",
    whiteSpace: "nowrap",
    border: "none",
    background: "none",
    transition: "color 0.15s, box-shadow 0.15s",
    "&:hover": { color: TAB_ACTIVE },
  },
  tabActive: {
    color: TAB_ACTIVE,
    fontWeight: 600,
    boxShadow: `inset 0 -2px 0 0 ${TAB_ACTIVE}`,
  },

  headerContainer: { margin: "20px 0 0", padding: "0 16px" },

  body: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  content: { flex: 1, padding: "0 16px", overflowY: "auto", minWidth: 0 },

  /* ── report header bar ── */
  reportBar: {
    background: NAV,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 18px",
    borderRadius: "8px",
    marginBottom: "14px",
    flexShrink: 0,
  },
  reportLogo: {
    border: "1.5px solid rgba(255,255,255,.5)",
    padding: "2px 10px",
    fontSize: "12px",
    fontWeight: 900,
    letterSpacing: "1px",
    borderRadius: "2px",
    background: "rgba(255,255,255,.08)",
    lineHeight: 1.3,
  },
  reportTitle: { fontSize: "14px", fontWeight: 700 },
  reportDate: { fontSize: "11px", color: "rgba(255,255,255,.75)" },

  /* ── table card & wrap ── */
  tableCard: {
    background: "#fff",
    borderRadius: 12,
    border: "1px solid #EBEBEB",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)",
    overflow: "hidden",
    marginBottom: 20,
    marginTop: 16,
  },
  tableSectionHead: {
    padding: "16px 20px 12px",
    borderBottom: "1px solid #E2E8F0",
  },
  tableSectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#111827",
    letterSpacing: "-0.01em",
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    margin: 0,
  },
  tableWrap: {
    overflowX: "auto",
    maxHeight: "calc(100vh - 280px)",
  },
  table: {
    borderCollapse: "collapse",
    borderSpacing: 0,
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 12,
    tableLayout: "fixed",
  },

  /* ── CYTN: fixed col header (rowSpan=2) ── */
  thFixed: {
    background: HEADER_BG,
    color: "rgba(255,255,255,.7)",
    padding: "0 12px",
    fontWeight: 700,
    fontSize: 10,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    textAlign: "left",
    whiteSpace: "nowrap",
    border: "none",
    position: "sticky",
    top: 0,
    zIndex: 5,
    boxSizing: "border-box",
    boxShadow: "inset 0 -2px 0 rgba(255,255,255,.22)",
  },
  /* ── band headers row 1 ── */
  thBand26: {
    background: BAND_26,
    color: "#fff",
    padding: "8px 12px",
    fontWeight: 700,
    fontSize: 11,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    textAlign: "center",
    border: "none",
    position: "sticky",
    top: 0,
    zIndex: 3,
    boxShadow: "inset 0 -2px 0 rgba(255,255,255,.22)",
  },
  thBand27: {
    background: BAND_27,
    color: "#fff",
    padding: "8px 12px",
    fontWeight: 700,
    fontSize: 11,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    textAlign: "center",
    border: "none",
    position: "sticky",
    top: 0,
    zIndex: 3,
    boxShadow: "inset 0 -2px 0 rgba(255,255,255,.22)",
  },
  thBandVar: {
    background: BAND_VAR,
    color: "#fff",
    padding: "8px 12px",
    fontWeight: 700,
    fontSize: 11,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    textAlign: "center",
    border: "none",
    position: "sticky",
    top: 0,
    zIndex: 3,
    boxShadow: "inset 0 -2px 0 rgba(255,255,255,.22)",
  },
  /* ── column name headers row 2 ── */
  thCol26: {
    background: BAND_26,
    color: "rgba(255,255,255,.7)",
    padding: "7px 10px",
    fontWeight: 600,
    fontSize: 10,
    letterSpacing: "0.05em",
    textAlign: "right",
    whiteSpace: "nowrap",
    border: "none",
    minWidth: 100,
    position: "sticky",
    top: 34,
    zIndex: 2,
    boxShadow: "inset 0 -1px 0 rgba(255,255,255,.12)",
  },
  thCol27: {
    background: BAND_27,
    color: "rgba(255,255,255,.7)",
    padding: "7px 10px",
    fontWeight: 600,
    fontSize: 10,
    letterSpacing: "0.05em",
    textAlign: "right",
    whiteSpace: "nowrap",
    border: "none",
    minWidth: 100,
    position: "sticky",
    top: 34,
    zIndex: 2,
    boxShadow: "inset 0 -1px 0 rgba(255,255,255,.12)",
  },
  thColVar: {
    background: BAND_VAR,
    color: "rgba(255,255,255,.7)",
    padding: "7px 10px",
    fontWeight: 600,
    fontSize: 10,
    letterSpacing: "0.05em",
    textAlign: "right",
    whiteSpace: "nowrap",
    border: "none",
    minWidth: 100,
    position: "sticky",
    top: 34,
    zIndex: 2,
    boxShadow: "inset 0 -1px 0 rgba(255,255,255,.12)",
  },
  thCol26First: {
    background: BAND_26,
    color: "rgba(255,255,255,.7)",
    padding: "7px 10px",
    fontWeight: 600,
    fontSize: 10,
    textAlign: "right",
    whiteSpace: "nowrap",
    border: "none",
    minWidth: 100,
    position: "sticky",
    top: 34,
    zIndex: 2,
    boxShadow: "inset 0 -1px 0 rgba(255,255,255,.12)",
  },
  thCol27First: {
    background: BAND_27,
    color: "rgba(255,255,255,.7)",
    padding: "7px 10px",
    fontWeight: 600,
    fontSize: 10,
    textAlign: "right",
    whiteSpace: "nowrap",
    border: "none",
    minWidth: 100,
    position: "sticky",
    top: 34,
    zIndex: 2,
    boxShadow: "inset 0 -1px 0 rgba(255,255,255,.12)",
  },
  thColVarFirst: {
    background: BAND_VAR,
    color: "rgba(255,255,255,.7)",
    padding: "7px 10px",
    fontWeight: 600,
    fontSize: 10,
    textAlign: "right",
    whiteSpace: "nowrap",
    border: "none",
    minWidth: 100,
    position: "sticky",
    top: 34,
    zIndex: 2,
    boxShadow: "inset 0 -1px 0 rgba(255,255,255,.12)",
  },

  /* ── CYTN data cells ── */
  tdFixed: {
    padding: "7px 12px",
    fontWeight: 500,
    color: "#111827",
    background: "#fff",
    borderBottom: "1px solid #E2E8F0",
    fontSize: 12,
    whiteSpace: "nowrap",
    overflow: "hidden",
    position: "sticky",
    zIndex: 2,
    boxShadow: "4px 0 8px -2px rgba(0,0,0,0.06)",
  },
  tdVal: {
    padding: "7px 10px",
    textAlign: "right",
    color: "#334155",
    background: "#fff",
    borderBottom: "1px solid #E2E8F0",
    fontSize: 12,
    whiteSpace: "nowrap",
    overflow: "hidden",
    fontVariantNumeric: "tabular-nums",
  },
  tdValFirst: {
    padding: "7px 10px",
    textAlign: "right",
    color: "#334155",
    background: "#fff",
    borderBottom: "1px solid #E2E8F0",
    fontSize: 12,
    whiteSpace: "nowrap",
    overflow: "hidden",
    fontVariantNumeric: "tabular-nums",
  },
  tdPct: {
    padding: "7px 10px",
    textAlign: "right",
    color: "#6B7280",
    background: "#fff",
    borderBottom: "1px solid #E2E8F0",
    fontSize: 12,
    whiteSpace: "nowrap",
    fontStyle: "italic",
  },
  /* ── CYTN total row ── */
  tdTotalFixed: {
    padding: "7px 12px",
    fontWeight: 700,
    color: "#111827",
    background: "#F0FFF4",
    borderBottom: "1px solid #CBD5E1",
    fontSize: 12,
    position: "sticky",
    zIndex: 2,
    boxShadow: "4px 0 8px -2px rgba(0,0,0,0.06)",
  },
  tdTotalVal: {
    padding: "7px 10px",
    textAlign: "right",
    fontWeight: 700,
    color: "#111827",
    background: "#F0FFF4",
    borderBottom: "1px solid #CBD5E1",
    fontSize: 12,
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },
  tdTotalValFirst: {
    padding: "7px 10px",
    textAlign: "right",
    fontWeight: 700,
    color: "#111827",
    background: "#F0FFF4",
    borderBottom: "1px solid #CBD5E1",
    fontSize: 12,
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },

  /* ── Full Plan table ── */
  thFP: {
    background: HEADER_BG,
    color: "rgba(255,255,255,.7)",
    padding: "9px 14px",
    fontWeight: 700,
    fontSize: 10,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    textAlign: "left",
    whiteSpace: "nowrap",
    border: "none",
    position: "sticky",
    top: 0,
    zIndex: 4,
    boxShadow: "inset 0 -2px 0 rgba(255,255,255,.22)",
  },
  thFPItd: {
    background: HEADER_BG,
    color: "rgba(255,255,255,.7)",
    padding: "9px 10px",
    fontWeight: 700,
    fontSize: 10,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    textAlign: "right",
    whiteSpace: "nowrap",
    border: "none",
    minWidth: 130,
    position: "sticky",
    top: 0,
    left: 160,
    zIndex: 5,
    boxShadow: "inset 0 -2px 0 rgba(255,255,255,.22)",
  },
  thFPNum: {
    background: HEADER_BG,
    color: "rgba(255,255,255,.7)",
    padding: "9px 10px",
    fontWeight: 700,
    fontSize: 10,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    textAlign: "right",
    whiteSpace: "nowrap",
    border: "none",
    minWidth: 110,
    position: "sticky",
    top: 0,
    zIndex: 3,
    boxShadow: "inset 0 -2px 0 rgba(255,255,255,.22)",
  },
  thFPTotal: {
    background: HEADER_BG,
    color: "#F59E0B",
    padding: "9px 10px",
    fontWeight: 700,
    fontSize: 10,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    textAlign: "right",
    whiteSpace: "nowrap",
    border: "none",
    minWidth: 120,
    position: "sticky",
    top: 0,
    zIndex: 3,
    boxShadow: "inset 0 -2px 0 rgba(255,255,255,.22)",
  },
  thFPEl: {
    background: HEADER_BG,
    color: "rgba(255,255,255,.7)",
    padding: "9px 14px",
    fontWeight: 700,
    fontSize: 10,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    textAlign: "left",
    whiteSpace: "nowrap",
    border: "none",
    minWidth: 160,
    position: "sticky",
    top: 0,
    left: 0,
    zIndex: 6,
    boxShadow: "inset 0 -2px 0 rgba(255,255,255,.22)",
  },
  tdFPEl: {
    padding: "7px 14px",
    fontWeight: 400,
    color: "#334155",
    background: "#fff",
    borderBottom: "1px solid #E2E8F0",
    fontSize: 12,
    whiteSpace: "nowrap",
    overflow: "hidden",
    position: "sticky",
    left: 0,
    zIndex: 2,
    boxShadow: "4px 0 8px -2px rgba(0,0,0,0.06)",
  },
  tdFPElBold: {
    padding: "7px 14px",
    fontWeight: 700,
    color: "#111827",
    background: "#F7FAFC",
    borderBottom: "1px solid #E2E8F0",
    fontSize: 12,
    whiteSpace: "nowrap",
    overflow: "hidden",
    position: "sticky",
    left: 0,
    zIndex: 2,
    boxShadow: "4px 0 8px -2px rgba(0,0,0,0.06)",
  },
  tdFPItd: {
    padding: "7px 10px",
    textAlign: "right",
    color: "#334155",
    background: "#fff",
    borderBottom: "1px solid #E2E8F0",
    fontSize: 12,
    whiteSpace: "nowrap",
    overflow: "hidden",
    fontVariantNumeric: "tabular-nums",
    position: "sticky",
    left: 160,
    zIndex: 2,
  },
  tdFPItdBold: {
    padding: "7px 10px",
    textAlign: "right",
    fontWeight: 700,
    color: "#111827",
    background: "#F7FAFC",
    borderBottom: "1px solid #E2E8F0",
    fontSize: 12,
    whiteSpace: "nowrap",
    overflow: "hidden",
    fontVariantNumeric: "tabular-nums",
    position: "sticky",
    left: 160,
    zIndex: 2,
  },
  tdFPVal: {
    padding: "7px 10px",
    textAlign: "right",
    color: "#334155",
    background: "#fff",
    borderBottom: "1px solid #E2E8F0",
    fontSize: 12,
    whiteSpace: "nowrap",
    overflow: "hidden",
    fontVariantNumeric: "tabular-nums",
  },
  tdFPValBold: {
    padding: "7px 10px",
    textAlign: "right",
    fontWeight: 700,
    color: "#111827",
    background: "#F7FAFC",
    borderBottom: "1px solid #E2E8F0",
    fontSize: 12,
    whiteSpace: "nowrap",
    overflow: "hidden",
    fontVariantNumeric: "tabular-nums",
  },
  tdFPTotal: {
    padding: "7px 10px",
    textAlign: "right",
    fontWeight: 700,
    color: "#F59E0B",
    background: "rgba(245,158,11,0.04)",
    borderBottom: "1px solid #E2E8F0",
    fontSize: 12,
    whiteSpace: "nowrap",
    overflow: "hidden",
    fontVariantNumeric: "tabular-nums",
  },
  tdFPTotalBold: {
    padding: "7px 10px",
    textAlign: "right",
    fontWeight: 700,
    color: "#F59E0B",
    background: "rgba(245,158,11,0.06)",
    borderBottom: "1px solid #E2E8F0",
    fontSize: 12,
    whiteSpace: "nowrap",
    overflow: "hidden",
    fontVariantNumeric: "tabular-nums",
  },
  tdFPEmpty: {
    padding: "7px 10px",
    background: "#fff",
    borderBottom: "1px solid #E2E8F0",
  },
  tdFPEmptyItd: {
    padding: "7px 10px",
    background: "#fff",
    borderBottom: "1px solid #E2E8F0",
    position: "sticky",
    left: 160,
    zIndex: 2,
  },

  /* placeholder */
  card: {
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 1px 6px rgba(0,0,0,.07), 0 0 0 1px rgba(0,0,0,.04)",
    overflow: "hidden",
    marginBottom: "14px",
  },
  cardHeader: {
    background: NAV,
    color: "#fff",
    textAlign: "center",
    fontSize: "13px",
    fontWeight: 700,
    letterSpacing: ".3px",
    padding: "9px 18px",
  },
  placeholder: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "64px 24px",
    gap: "12px",
    color: "#94A3B8",
  },
  placeholderTitle: { fontSize: "16px", fontWeight: 600, color: "#64748B" },
  placeholderSub: { fontSize: "13px" },
}));

/* ══════════════════════════════════════════════════════════════ */
/*  Current Year to Next Year tab                                 */
/* ══════════════════════════════════════════════════════════════ */

/* ══════════════════════════════════════════════════════════════ */
/*  Full Plan over Year tab                                       */
/* ══════════════════════════════════════════════════════════════ */
// eslint-disable-next-line no-unused-vars
const FP_YEARS = [
  2025, 2026, 2027, 2028, 2029, 2030, 2031, 2032, 2033, 2034, 2035, 2036,
];
// eslint-disable-next-line no-unused-vars
const N2 = null;

// single:true  → one <tr> with ITD + all year values
// single:false → two <tr>s: (label+ITD) then (empty+year values)
// bold         → use bold/shaded cell styles
// isPct        → year values formatted as "XX.X%", itd displayed raw
// dp           → decimal places for year values (default 1)
// eslint-disable-next-line no-unused-vars
const FP_ROWS = [
  {
    label: "Revenue",
    isCurrency: true,
    itd: 2461358715.86,
    ys: [
      340018038.5, 368620359.7, 410623429.9, 389022693.1, 322293479.8,
      290914541.2, 204194248.1, 30803307.2, 4292607.5, 2527060.2, 2381650.0,
      2424200.0,
    ],
    total: 2995690565.2,
    single: true,
  },
  {
    label: "Direct Labor",
    isCurrency: true,
    itd: 828961038.15,
    ys: [
      130848241.1, 148100285.1, 161539815.9, 156970062.2, 129620750.6,
      114900255.9, 80695587.7, 12455560.1, 2091916.7, 1033591.8, 928869.5,
      945464.5,
    ],
    total: 1137505231.0,
    single: true,
  },
  {
    label: "Subcontractor",
    isCurrency: true,
    itd: 621180972.35,
    ys: [
      56789681.2, 73915406.0, 86814243.0, 78440782.8, 64620239.5, 60895264.8,
      41692743.3, 6015598.2, 480115.4, 491224.8, 500160.5, 509096.3,
    ],
    total: 589844820.4,
    single: true,
  },
  {
    label: "ODCs",
    isCurrency: true,
    itd: 94768515.39,
    ys: [
      5084784.6, 5115531.7, 1562561.8, 614415.0, 8836.3, 0.0, 0.0, 0.0, 0.0,
      0.0, 0.0, 0.0,
    ],
    total: 18911489.7,
    single: true,
  },
  {
    label: "Total Direct Costs",
    isCurrency: true,
    itd: 1544910525.89,
    ys: [
      192722706.8, 227131222.7, 249916620.7, 236025260.0, 194249826.5,
      175795520.7, 122388331.0, 18471158.2, 2572032.1, 1524816.6, 1429030.0,
      1454560.7,
    ],
    total: 1746261541.1,
    single: true,
    bold: true,
  },
  {
    label: "Gross Profit",
    isCurrency: true,
    itd: 916448189.97,
    ys: [
      147295331.65, 141489136.96, 160706809.19, 52997433.09, 128043653.38,
      115119020.5, 81805917.13, 12332148.96, 1720575.36, 1002243.55, 952620.01,
      969639.28,
    ],
    total: 1249429024.14,
    single: true,
  },
  {
    label: "Gross Margins %",
    itd: "0.37",
    ys: [
      38.6, 38.4, 39.1, 39.3, 39.7, 39.6, 40.1, 40.0, 40.1, 39.7, 40.0, 40.0,
    ],
    total: 41.7,
    single: false,
    isPct: true,
  },
  {
    label: "Total Project Hours",
    itd: 12547560.75,
    ys: [
      2733307.8,
      2695601.5,
      1100341.9,
      645620.0,
      283757.9,
      162882.3,
      31794.9,
      17078.6,
      15363.5,
      1367.0,
      N2,
      N2,
    ],
    total: 12547560.8,
    single: false,
  },
  {
    label: "IB Employee Hours",
    itd: 9629338.51,
    ys: [
      1880212.5,
      2049357.3,
      810952.7,
      497734.5,
      232850.3,
      121951.6,
      28513.8,
      17078.6,
      15363.5,
      1367.0,
      N2,
      N2,
    ],
    total: 9629338.5,
    single: false,
  },
  {
    label: "Subk Hours",
    itd: 2918222.24,
    ys: [
      853095.3,
      646244.2,
      289389.2,
      147885.4,
      50907.5,
      40930.6,
      3281.0,
      N2,
      N2,
      N2,
      N2,
      N2,
    ],
    total: 2918222.2,
    single: false,
  },
  {
    label: "FTE",
    itd: 90.66,
    ys: [
      43.89, 54.94, 146.81, 243.13, 456.8, 705.42, 2538.01, 729.31, 136.16,
      756.1, 0.0, 0.0,
    ],
    total: 90.66,
    single: false,
    dp: 2,
  },
];

function PlaceholderTab({ label, classes }) {
  return (
    <div className={classes.card}>
      <div className={classes.cardHeader}>{label}</div>
      <div className={classes.placeholder}>
        <svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#CBD5E1"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <line x1="3" y1="9" x2="21" y2="9" />
          <line x1="9" y1="21" x2="9" y2="9" />
        </svg>
        <span className={classes.placeholderTitle}>{label}</span>
        <span className={classes.placeholderSub}>Content coming soon</span>
      </div>
    </div>
  );
}

function GlobalFilterIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="8" y1="12" x2="16" y2="12" />
      <line x1="10" y1="18" x2="14" y2="18" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  Horizontal filter bar (matches Scenario MoM)                  */
/* ══════════════════════════════════════════════════════════════ */
function HorizontalFilterBar({
  visibleKeys,
  draft,
  dropdownOptions,
  onChange,
  onClearAll,
  onApply,
  onClose,
  classes,
  clearAllDisabled,
  applyDisabled,
}) {
  const filters = FILTERS_CONFIG.filter((f) => visibleKeys.includes(f.key));
  const selectStyle = { minWidth: 110, maxWidth: 180, fontSize: 12 };
  return (
    <div className={classes.hFilterBar}>
      {filters.map((f) => {
        const opts = toFilterSelectOptions(
          f.key,
          getFilterFieldOptions(f, dropdownOptions),
        );
        const val = draft[f.key];
        return (
          <div key={f.key} className={classes.hFilterGroup}>
            <span className={classes.hFilterLabel}>{f.label}</span>
            {f.type === "select" && (
              <Select
                size="small"
                style={selectStyle}
                value={val || (f.key === "year" ? opts[0]?.value : "All")}
                onChange={(v) =>
                  onChange(f.key, v === "All" ? f.defaultValue : v)
                }
                options={
                  f.key === "year"
                    ? opts
                    : [{ value: "All", label: "All" }, ...opts]
                }
                {...FILTER_SELECT_SEARCH_PROPS}
                popupMatchSelectWidth={false}
              />
            )}
            {f.type === "multiselect" && (
              <GlobalMultiSelectFilter
                size="small"
                style={selectStyle}
                value={val}
                options={opts}
                onChange={(v) => onChange(f.key, v)}
                {...FILTER_SELECT_SEARCH_PROPS}
                popupMatchSelectWidth={false}
              />
            )}
            {f.type === "text" && (
              <input
                className={classes.hFilterInput}
                value={val || ""}
                placeholder="All"
                onChange={(e) => onChange(f.key, e.target.value)}
              />
            )}
            {f.type === "daterange" && (
              <HorizontalDateRangeFilter
                value={val}
                onChange={(next) => onChange(f.key, next)}
              />
            )}
          </div>
        );
      })}
      <div className={classes.hFilterActions}>
        <button
          className={classes.hFilterClearAll}
          onClick={onClearAll}
          disabled={clearAllDisabled}
        >
          Clear All
        </button>
        <FilterApplyButton
          draft={draft}
          onApply={onApply}
          className={classes.hFilterApply}
          disabled={applyDisabled}
        >
          Apply
        </FilterApplyButton>
      </div>
      <button
        className={classes.hFilterClose}
        onClick={onClose}
        title="Close Filters"
      >
        ✕
      </button>
    </div>
  );
}

export default function ScenarioYoY() {
  const classes = useStyles();
  const plClasses = usePlByProgramStyles();
  useReportFonts();
  const history = useHistory();
  const { tab: tabParam } = useParams();
  const activeTab = TABS.find((t) => t.key === tabParam)?.key ?? TABS[0].key;
  const [filterOpen, setFilterOpen] = useState(true);
  const [appliedFilters, setAppliedFilters] = useState(initFilterValues);
  const [draftFilters, setDraftFilters] = useState(initFilterValues);
  const [downloading, setDownloading] = useState(false);

  const [cytnLoading, setCytnLoading] = useState(true);
  const [cytnData, setCytnData] = useState(null);
  const [fpLoading, setFpLoading] = useState(true);
  const [fpData, setFpData] = useState(null);

  /* Full Plan Over Year already shows every year as its own column — a
     single-year filter doesn't apply to it. */
  const visibleKeys = useMemo(
    () =>
      activeTab === "full_plan"
        ? YOY_FILTERS.filter((k) => k !== "year")
        : YOY_FILTERS,
    [activeTab],
  );
  const { dropdownOptions, relationshipRows, effectiveFilters } = useExpandedAppliedFilters(
    appliedFilters,
    draftFilters,
    visibleKeys,
  );

  const apiFilterKey = useMemo(
    () => getApiFilterKey(effectiveFilters, buildWaterfallEacApiFilters),
    [effectiveFilters],
  );

  useEffect(() => {
    if (filterOpen) {
      setDraftFilters(
        pruneInvalidCascadeSelections(
          appliedFilters,
          relationshipRows,
          visibleKeys,
        ),
      );
    }
  }, [filterOpen, appliedFilters, relationshipRows, visibleKeys]);

  useEffect(() => {
    if (activeTab !== "current_to_next") return;
    setCytnLoading(true);
    setCytnData(null);
    getScenarioYoyTwoYearComparisonAPI(
      buildWaterfallEacApiFilters(effectiveFilters),
    )
      .then((res) => setCytnData(transformTwoYear(res?.data ?? res)))
      .catch(() => {})
      .finally(() => setCytnLoading(false));
  }, [activeTab, apiFilterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab !== "full_plan") return;
    setFpLoading(true);
    setFpData(null);
    getScenarioYoyFullPlanOverYearAPI(
      buildWaterfallEacApiFilters(effectiveFilters),
    )
      .then((res) => setFpData(transformFullPlan(res?.data ?? res)))
      .catch(() => {})
      .finally(() => setFpLoading(false));
  }, [activeTab, apiFilterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const filterCount = getActiveFilterCount(appliedFilters, visibleKeys);

  const activeChips = FILTERS_CONFIG.filter(
    (f) =>
      visibleKeys.includes(f.key) && isFilterActive(f, appliedFilters[f.key]),
  );

  const isOnlyDefaultYear = (fv) => {
    const keys = visibleKeys.filter((k) => k !== "year");
    return (
      !keys.some((k) => {
        const f = FILTERS_CONFIG.find((fc) => fc.key === k);
        return f && isFilterActive(f, fv[k]);
      }) && String(fv.year) === "2026"
    );
  };

  const clearChip = useCallback((f) => {
    setAppliedFilters((prev) => ({ ...prev, [f.key]: getDefaultValue(f) }));
    setDraftFilters((prev) => ({ ...prev, [f.key]: getDefaultValue(f) }));
  }, []);

  const clearAllApplied = useCallback(() => {
    const reset = (prev) => {
      const next = { ...prev };
      visibleKeys.forEach((k) => {
        const f = FILTERS_CONFIG.find((fc) => fc.key === k);
        if (f) next[k] = getDefaultValue(f);
      });
      return next;
    };
    setAppliedFilters(reset);
    setDraftFilters(reset);
  }, [visibleKeys]);

  const handleDraftChange = useCallback(
    (key, value) => {
      setDraftFilters((prev) =>
        applyCascadeDraftChange(
          prev,
          key,
          value,
          relationshipRows,
          visibleKeys,
        ),
      );
    },
    [relationshipRows, visibleKeys],
  );

  const handleApply = useCallback(() => {
    setAppliedFilters(
      pruneInvalidCascadeSelections(
        draftFilters,
        relationshipRows,
        visibleKeys,
      ),
    );
  }, [draftFilters, relationshipRows, visibleKeys]);

  const clearAll = useCallback(() => {
    setDraftFilters((prev) => {
      const next = { ...prev };
      visibleKeys.forEach((k) => {
        const f = FILTERS_CONFIG.find((fc) => fc.key === k);
        if (f) next[k] = getDefaultValue(f);
      });
      return next;
    });
  }, [visibleKeys]);

  const actualsDate = (month) =>
    month ? `Actuals through ${MONTH_ABBR[month - 1]} ${new Date().getFullYear()}` : "";

  const TAB_HEADER = {
    current_to_next: {
      eyebrow: "Scenario Comparison YoY",
      title: "Current Year to Next Year",
      accent: cytnData?.fiscalYear1
        ? `FY${cytnData.fiscalYear1} Vs FY${cytnData.fiscalYear2}`
        : "YoY Forecast",
      badge: "Live Forecast",
      date: actualsDate(cytnData?.actualThroughMonth),
    },
    full_plan: {
      eyebrow: "Scenario Comparison YoY",
      title: "Full Plan over Year",
      accent: "Full Year Outlook",
      badge: "Live Forecast",
      date: actualsDate(fpData?.actualThroughMonth),
    },
  };

  const handleDownload = useCallback(async () => {
    const entry = DOWNLOAD_BY_TAB[activeTab];
    if (!entry || downloading) return;
    setDownloading(true);
    try {
      const { blob, filename } = await entry.api(effectiveFilters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || entry.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Scenario Comparison YoY download failed:", err);
    } finally {
      setDownloading(false);
    }
  }, [activeTab, effectiveFilters, downloading]);

  return (
    <div className={classes.page}>
      {downloading && <Loading />}
      {/* ── Top bar ── */}
      <div className={classes.topBar}>
        <BackButton
          onClick={() => goToReportsCatalog(history)}
          ariaLabel="Back to Reports"
        />
        <nav className={classes.crumb}>
          <span
            className={classes.crumbLink}
            onClick={() => goToReportsCatalog(history)}
          >
            Reports
          </span>
          <span className={classes.crumbSep}>&gt;</span>
          <span className={classes.crumbCur}>Scenario Comparison YoY</span>
        </nav>
        <div className={classes.topActions}>
          <button
            type="button"
            className={`${classes.actionBtn} ${classes.actionBtnIconOnly} ${classes.actionBtnActive}`}
            title="Download as Excel"
            aria-label="Download Report"
            onClick={handleDownload}
            disabled={downloading}
          >
            <DownloadIcon />
          </button>
          <button
            type="button"
            className={`${classes.actionBtn} ${filterOpen || filterCount > 0 ? classes.actionBtnActive : ""}`}
            onClick={() => setFilterOpen((p) => !p)}
          >
            <GlobalFilterIcon />
            Filters
            {filterCount > 0 && (
              <span className={classes.filterBadge}>{filterCount}</span>
            )}
          </button>
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div className={classes.tabBar}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`${classes.tab} ${activeTab === tab.key ? classes.tabActive : ""}`}
            onClick={() =>
              history.push(`/projects/reports/scenario_yoy/${tab.key}`)
            }
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Horizontal filter bar ── */}
      {filterOpen && (
        <HorizontalFilterBar
          visibleKeys={visibleKeys}
          draft={draftFilters}
          dropdownOptions={dropdownOptions}
          onChange={handleDraftChange}
          onClearAll={clearAll}
          onApply={handleApply}
          applyDisabled={areVisibleFilterValuesEqual(draftFilters, appliedFilters, visibleKeys)}
          onClose={() => setFilterOpen(false)}
          classes={classes}
          clearAllDisabled={isOnlyDefaultYear(draftFilters)}
        />
      )}

      {/* ── Active filter chips ── */}
      {!filterOpen && activeChips.length > 0 && (
        <div className={classes.chipsBar}>
          {activeChips.map((f) => {
            const val = appliedFilters[f.key];
            const tip =
              f.type === "multiselect" && Array.isArray(val) && val.length > 1
                ? `${f.label}: ${val.join(", ")}`
                : `${f.label}: ${getDisplayValue(f, val)}`;
            return (
              <span key={f.key} className={classes.chip} title={tip}>
                <span className={classes.chipLabel}>{f.label}:</span>
                {getDisplayValue(f, val)}
                <button
                  className={classes.chipClear}
                  onClick={() => clearChip(f)}
                  title={`Clear ${f.label}`}
                  disabled={f.key === "year" && String(val) === "2026"}
                >
                  ✕
                </button>
              </span>
            );
          })}
          <button
            className={classes.chipsClearAll}
            onClick={clearAllApplied}
            disabled={isOnlyDefaultYear(appliedFilters)}
          >
            Clear All
          </button>
        </div>
      )}

      {/* ── Hero header ── */}
      <div className={classes.headerContainer}>
        <PlPageHeader
          classes={plClasses}
          eyebrow={TAB_HEADER[activeTab].eyebrow}
          title={TAB_HEADER[activeTab].title}
          accent={TAB_HEADER[activeTab].accent}
          badge={TAB_HEADER[activeTab].badge}
          date={TAB_HEADER[activeTab].date}
        />
      </div>

      {/* ── Body ── */}
      <div className={classes.body}>
        <div className={classes.content}>
          {activeTab === "current_to_next" &&
            (cytnLoading ? (
              <Loading />
            ) : (
              <CurrentYearToNextTabComponent
                rows={cytnData?.rows ?? []}
                totalRow={cytnData?.totalRow ?? {}}
                fiscalYear1={cytnData?.fiscalYear1}
                fiscalYear2={cytnData?.fiscalYear2}
              />
            ))}
          {activeTab === "full_plan" &&
            (fpLoading ? (
              <Loading />
            ) : (
              <FullPlanTabComponent
                FP_YEARS={fpData?.years ?? []}
                FP_ROWS={fpData?.rows ?? []}
              />
            ))}
        </div>
      </div>
    </div>
  );
}
